import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import metricsTracker from '../utils/metricsTracker';

const LandingPage = ({ onGetStarted }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [globalMetrics, setGlobalMetrics] = useState({ papersSaved: 0, treesSaved: 0, co2Saved: 0 });
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  // Load global metrics from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('bellMathsMetrics');
    if (saved) {
      const metrics = JSON.parse(saved);
      setGlobalMetrics(metrics);
    }
  }, []);

  // Admin key combination handler
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ctrl + Shift + A to show admin panel
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        setShowAdminPanel(!showAdminPanel);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showAdminPanel]);

  // Admin functions
  const handleDownloadMetrics = () => {
    metricsTracker.downloadAllMetrics();
  };

  const handleClearMetrics = () => {
    if (window.confirm('Are you sure you want to clear all metrics data? This cannot be undone.')) {
      localStorage.removeItem('mathPracticeMetrics');
      metricsTracker.clearMetrics(); // Also clear the duplicate prevention cache
      alert('All metrics data has been cleared.');
    }
  };

  const handleShowMetrics = () => {
    const allMetrics = metricsTracker.loadMetricsFromStorage();
    console.log('Current metrics:', allMetrics);
    alert(`Current metrics count: ${allMetrics.length}\nCheck browser console for details.`);
  };

  const handleContactUs = () => {
    setShowContactModal(true);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
  };


  // Calculate display metrics
  const calculateDisplayMetrics = () => {
    const papersSaved = Number(globalMetrics.papersSaved) || 0;
    const treesSaved = (papersSaved / 100).toFixed(2); // 1 tree per 100 papers
    const co2Saved = (papersSaved * 0.5).toFixed(2); // 0.5 kg CO2 per paper
    return { papersSaved, treesSaved, co2Saved };
  };

  // Carousel data
  const carouselData = [
    {
      id: 1,
      title: "Save Paper, Save Earth",
      description: "Join thousands of students saving paper while mastering math",
      icon: "🌱",
      color: "#4CAF50",
      animation: "fadeInLeft"
    },
    {
      id: 2,
      title: "Interactive Math Learning",
      description: "Solve problems step-by-step with our virtual math editor",
      icon: "✏️",
      color: "#2196F3",
      animation: "fadeInUp"
    },
    {
      id: 3,
      title: "Practice & Exam Mode",
      description: "Choose between practice sessions or timed exams",
      icon: "📚",
      color: "#FF9800",
      animation: "fadeInRight"
    },
    {
      id: 4,
      title: "Real-time Progress Tracking",
      description: "Monitor your improvement with detailed analytics",
      icon: "📊",
      color: "#9C27B0",
      animation: "fadeInDown"
    }
  ];

  // Pricing plans
  const pricingPlans = [
    {
      name: "Basic(Free)",
      price: "$0",
      period: "forever",
      features: [
        "Basic math operations",
        "5 practice problems per day",
        "Standard virtual keyboard",
        "Basic progress tracking"
      ],
      color: "#4CAF50",
      popular: false
    },
    {
      name: "Pro",
      price: "$9.99",
      period: "per month",
      features: [
        "All math operations",
        "Unlimited practice problems",
        "Advanced Operations",
        "Detailed progress reports",
        "Exam mode with timer",
        "Print worksheets"
      ],
      color: "#2196F3",
      popular: true
    },
    {
      name: "For Schools",
      price: "$19.99",
      period: "per month",
      features: [
        "Everything in Pro",
        "Custom problem generation",
        "Advanced analytics dashboard",
        "Parent/Teacher portal",
        "Multiple student accounts",
        "Priority support",
        "API access"
      ],
      color: "#FF9800",
      popular: false
    }
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Shruthi P",
      role: "Grade 6, CBSE, Bangalore",
      content: "BellMaths made division so much fun! I love the virtual keyboard and step-by-step solutions.",
      avatar: "👩",
      rating: 5
    },
    {
      name: "Madhura G",
      role: "Parent",
      content: "My daughter's math skills improved dramatically. The paper-saving feature is a bonus for the environment!",
      avatar: "👨",
      rating: 5
    },
    {
      name: "David Gass",
      role: "Grade 5, New York",
      content: "The exam mode helped me prepare for my school tests. I love how it tracks my progress!",
      avatar: "👩",
      rating: 5
    },
    {
      name: "Ananya M",
      role: "Math Teacher",
      content: "As a teacher, I appreciate the detailed step-by-step solutions. Great tool for classroom use!",
      avatar: "👨‍🏫",
      rating: 5
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselData.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [carouselData.length]);

  // Fade in animation
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Navigation functions
  const navigateToOperation = (operation) => {
    // Navigate to the main app and set the operation to practice mode
    onGetStarted(operation, 'practice');
  };

  const navigateToExam = (operation) => {
    // Navigate to the main app and set the operation to exam mode
    onGetStarted(operation, 'exam');
  };

  return (
    <div className="landing-page">
      {/* Floating Math Elements Background */}
      <div className="floating-elements">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="floating-element"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          >
            {['+', '−', '×', '÷', '=', '√', 'π', '∞'][Math.floor(Math.random() * 8)]}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="landing-header">
        <div className="header-content">
          <div className="logo">
            <img 
              src="https://encycdata.com/static/media/logo.f8886f3d8aa8acd1ba80.png" 
              alt="Encycdata Logo" 
              style={{
                height: '45px',
                width: 'auto',
                borderRadius: '6px',
                boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
                backgroundColor: 'white',
                padding: '4px',
                filter: 'brightness(1.1) contrast(1.2)'
              }}
            />
            <div className="logo-text">
              <h1>BellMaths</h1>
            </div>
          </div>
          <div className="brand-message">
            Go Green! MoreSustainable and Eco-friendly education!
          </div>
          <nav className="header-nav">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#testimonials">Testimonials</a>
            <button className="btn btn-primary" onClick={() => onGetStarted('addition', 'practice')}>
              Get Started
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Practice Math With <span className="highlight">Seamless Paper-Pencil Experience</span>
            </h1>
            <div className="hero-features">
              <div className="feature-item">
                <span className="feature-bullet">✓</span>
                <span className="feature-text">Self-service platform with Robust Math Editor</span>
              </div>
              <div className="feature-item">
                <span className="feature-bullet">✓</span>
                <span className="feature-text">Practice, Validate, Save and Revise anytime</span>
              </div>
              <div className="feature-item">
                <span className="feature-bullet">✓</span>
                <span className="feature-text">Reduce burden for parents and tutors</span>
              </div>
              <div className="feature-item">
                <span className="feature-bullet">✓</span>
                <span className="feature-text">Support for Basic to complex problems</span>
              </div>
            </div>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-large" onClick={() => onGetStarted('addition', 'practice')}>
                Start Practice
              </button>
              <div className="tree-image">
                <span style={{ fontSize: '60px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
                  🌳
                </span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="notebook-container">
              <div className="notebook-page active">
                <div className="notebook-header">
                  <div className="notebook-title">BellMaths Notebook</div>
                  <div className="notebook-date">Date: {new Date().toLocaleDateString()}</div>
                </div>
                
                {/* Environmental Message and Operation Buttons */}
                <div className="notebook-content">
                  {/* Environmental Message */}
                  <div className="environmental-message">
                    <h3>Solve the following problems:</h3>
                    <p className="tree-facts">
                      A standard pine tree, with 45ft of the usable trunk and a diameter of 8 inches, will produce around 10,000 sheets(20 ream) of paper. Recycled paper significantly reduces the demand for virgin pulp, water and energy which in turn helps conserve forests and our environment
                    </p>
                  </div>
                  
                  {/* Operation Buttons on Notebook - Single Row */}
                  <div className="notebook-operations">
                    <div className="operations-grid">
                      <div className="operation-item">
                        <div className="operation-symbol" style={{ color: '#4CAF50' }}>➕</div>
                        <div className="operation-buttons">
                          <button className="btn btn-outline btn-small" onClick={() => navigateToOperation('addition')}>
                            Practice
                          </button>
                          <button className="btn btn-primary btn-small" onClick={() => navigateToExam('addition')}>
                            Exam
                          </button>
                        </div>
                      </div>
                      
                      <div className="operation-item">
                        <div className="operation-symbol" style={{ color: '#FF6B6B' }}>➖</div>
                        <div className="operation-buttons">
                          <button className="btn btn-outline btn-small" onClick={() => navigateToOperation('subtraction')}>
                            Practice
                          </button>
                          <button className="btn btn-primary btn-small" onClick={() => navigateToExam('subtraction')}>
                            Exam
                          </button>
                        </div>
                      </div>
                      
                      <div className="operation-item">
                        <div className="operation-symbol" style={{ color: '#9C27B0' }}>✖️</div>
                        <div className="operation-buttons">
                          <button className="btn btn-outline btn-small" onClick={() => navigateToOperation('multiplication')}>
                            Practice
                          </button>
                          <button className="btn btn-primary btn-small" onClick={() => navigateToExam('multiplication')}>
                            Exam
                          </button>
                        </div>
                      </div>
                      
                      <div className="operation-item">
                        <div className="operation-symbol" style={{ color: '#2196F3' }}>➗</div>
                        <div className="operation-buttons">
                          <button className="btn btn-outline btn-small" onClick={() => navigateToOperation('division')}>
                            Practice
                          </button>
                          <button className="btn btn-primary btn-small" onClick={() => navigateToExam('division')}>
                            Exam
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Paper Saving Metrics Section */}
      <section className="metrics-section">
        <div className="metrics-container">
          <h2>Our Environmental Impact</h2>
          <p>Every student who uses BellMaths saves paper and contributes to a greener planet.</p>
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Papers Saved</h3>
              <p>{calculateDisplayMetrics().papersSaved}</p>
              <p>papers saved</p>
            </div>
            <div className="metric-card">
              <h3>Trees Saved</h3>
              <p>{calculateDisplayMetrics().treesSaved}</p>
              <p>trees</p>
            </div>
            <div className="metric-card">
              <h3>CO2 Saved</h3>
              <p>{calculateDisplayMetrics().co2Saved}</p>
              <p>kg CO2</p>
            </div>
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="carousel-section" id="features">
        <div className="carousel-container">
          <div className="carousel-header">
            <h2>BellMaths - Online Notebook Features</h2>
            <p>Discover the features that make learning math engaging and effective</p>
          </div>
          
          <div className="carousel">
            <div className="carousel-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {carouselData.map((slide, index) => (
                <div key={slide.id} className="carousel-slide">
                  <div className="slide-content" style={{ borderColor: slide.color }}>
                    <div className="slide-icon" style={{ backgroundColor: slide.color }}>
                      {slide.icon}
                    </div>
                    <h3>{slide.title}</h3>
                    <p>{slide.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="carousel-indicators">
              {carouselData.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="pricing-container">
          <h2>Choose Your Plan</h2>
          <p>Start free and upgrade as you grow</p>
          
          <div className="pricing-grid">
            {pricingPlans.map((plan) => (
              <div 
                key={plan.name} 
                className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                style={{ borderColor: plan.color }}
              >
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                <button 
                  className="btn btn-primary btn-large"
                  style={{ backgroundColor: plan.color }}
                  onClick={handleContactUs}
                >
                  Contact Us
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section" id="testimonials">
        <div className="testimonials-container">
          <h2>What Our Users Say</h2>
          <p>Real feedback from students, parents, and teachers</p>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="avatar">{testimonial.avatar}</div>
                  <div className="testimonial-info">
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                  <div className="rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="star">⭐</span>
                    ))}
                  </div>
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin Panel */}
      {showAdminPanel && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          border: '2px solid #e74c3c',
          borderRadius: '8px',
          padding: '20px',
          zIndex: 1000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          minWidth: '300px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: '#e74c3c', margin: '0 0 10px 0' }}>🔧 Admin Panel</h3>
            <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
              Press Ctrl + Shift + A to toggle
            </p>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={handleDownloadMetrics}
              style={{
                padding: '10px 15px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              📊 Download All Metrics (CSV)
            </button>
            
            <button
              onClick={handleShowMetrics}
              style={{
                padding: '10px 15px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              🔍 Show Current Metrics
            </button>
            
            <button
              onClick={handleClearMetrics}
              style={{
                padding: '10px 15px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              🗑️ Clear All Metrics Data
            </button>
            
            <button
              onClick={() => setShowAdminPanel(false)}
              style={{
                padding: '10px 15px',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              ❌ Close Panel
            </button>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '15px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ 
              marginBottom: '20px', 
              color: '#2c3e50',
              fontSize: '1.8rem'
            }}>
              Get in Touch
            </h2>
            <p style={{ 
              marginBottom: '30px', 
              color: '#7f8c8d',
              fontSize: '1rem'
            }}>
              Choose your preferred way to contact us
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {/* Phone */}
              <a 
                href="tel:+917708059930"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '15px 20px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
              >
                <span style={{ fontSize: '1.5rem' }}>📱</span>
                <span>+91 77080 59930</span>
              </a>

              {/* Email */}
              <a 
                href="mailto:admin@encycdata.com"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '15px 20px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#c0392b'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#e74c3c'}
              >
                <span style={{ fontSize: '1.5rem' }}>📧</span>
                <span>admin@encycdata.com</span>
              </a>

              {/* WhatsApp */}
              <a 
                href="https://wa.me/918807349930"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '15px 20px',
                  backgroundColor: '#25d366',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#128c7e'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#25d366'}
              >
                <span style={{ fontSize: '1.5rem' }}>💬</span>
                <span>WhatsApp: +91 88073 49930</span>
              </a>
            </div>

            <button
              onClick={closeContactModal}
              style={{
                marginTop: '25px',
                padding: '10px 20px',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'background-color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#7f8c8d'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#95a5a6'}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-container">
          <h2>Get in Touch</h2>
          <p>Have questions or need support? We're here to help!</p>
          <div className="contact-options">
            <div className="contact-option">
              <div className="contact-icon-large">📱</div>
              <h3>Call Us</h3>
              <a href="tel:+917708059930" className="contact-number">
                +91 77080 59930
              </a>
            </div>
            <div className="contact-option">
              <div className="contact-icon-large">📧</div>
              <h3>Email Us</h3>
              <a href="mailto:admin@encycdata.com" className="contact-email">
                admin@encycdata.com
              </a>
            </div>
            <div className="contact-option">
              <div className="contact-icon-large">💬</div>
              <h3>WhatsApp</h3>
              <a 
                href="https://wa.me/918807349930" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-whatsapp"
              >
                Chat with us on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>BellMaths</h3>
            <p>Making math learning interactive, engaging, and environmentally friendly.</p>
          </div>
          <div className="footer-section">
            <h4>Support</h4>
            <ul>
              <li>Help Center</li>
              <li>Contact Us</li>
              <li>Privacy Policy</li>
              <li>Terms of Service</li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact Us</h4>
            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">📱</span>
                <a href="tel:+917708059930" className="contact-link">
                  +91 77080 59930
                </a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <a href="mailto:admin@encycdata.com" className="contact-link">
                  admin@encycdata.com
                </a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">💬</span>
                <a 
                  href="https://wa.me/918807349930" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="contact-link whatsapp-link"
                >
                  WhatsApp: +91 88073 49930
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 BellMaths. All rights reserved. 🌱 Go Green Initiative</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
